import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus } from '@digital-twins/design-system/icons'

import { CollectionCreateDialog } from '@/components/library/CollectionCreateDialog'
import { MosaicCollectionCard } from '@/components/library/MosaicCollectionCard'
import { Button } from '@/components/ui/button'
import { KnowledgePageShell } from '@/layouts/KnowledgePageShell/KnowledgePageShell'
import {
  LIBRARY_COLLECTION_GRID_CLASS,
  LibraryCollectionsSkeleton,
} from '@/pages/KnowledgeHubPage/collections/LibraryCollectionsGrid'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'

import { useLibraryCollectionsList } from './useLibraryCollectionsList'

/**
 * Every collection the viewer can see, at `ROUTES.knowledgeCollections`: a
 * paged card grid with "Show more" (cursor paging, 50 per page), in
 * `KnowledgePageShell` with the Topic page's header language.
 */
export default function KnowledgeCollectionsListPage() {
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const list = useLibraryCollectionsList(true)

  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const onSaved = (token: string) => {
    setIsCreateOpen(false)
    navigate(ROUTES.knowledgeCollection(token))
  }

  const titleClass =
    'min-w-0 font-display text-[36px] font-normal leading-none tracking-[-0.01em] text-foreground @[760px]/collections:text-5xl'

  let body = null
  if (list.collections.length === 0 && list.isLoading) {
    body = <LibraryCollectionsSkeleton count={6} testId="knowledge-collections-loading" />
  } else if (list.collections.length === 0 && list.hasError) {
    body = (
      <div className="flex flex-wrap items-center gap-3" data-testid="knowledge-collections-error">
        <p className="text-sm text-muted-foreground">
          {t('library.collections.list.loadFailed')}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          shape="pill"
          className="h-8 px-3"
          onClick={() => void list.refresh()}
          data-testid="knowledge-collections-retry"
        >
          {t('library.collections.tryAgain')}
        </Button>
      </div>
    )
  } else if (list.collections.length === 0) {
    body = (
      <p className="text-sm text-muted-foreground" data-testid="knowledge-collections-empty">
        {t('library.collections.home.empty')}
      </p>
    )
  } else {
    body = (
      <div className="flex flex-col gap-6">
        <div className={LIBRARY_COLLECTION_GRID_CLASS} data-testid="knowledge-collections-grid">
          {list.collections.map((collection, index) => (
            <MosaicCollectionCard key={collection.token} collection={collection} index={index} />
          ))}
        </div>
        {list.hasMore ? (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-9 px-4"
              disabled={list.isLoadingMore}
              onClick={list.loadMore}
              data-testid="knowledge-collections-show-more"
            >
              {t('library.collections.showMore')}
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <KnowledgePageShell
      testId="knowledge-collections"
      overlays={
        <CollectionCreateDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} mode="create" entry="list" onSaved={onSaved} />
      }
    >
      {/* Container queries only below this root: the list's own breakpoints match the Topic page's. */}
      <div className="@container/collections">
        <div className="@container/library flex min-w-0 flex-col">
          <header className="text-left" data-testid="knowledge-collections-header">
            <div className="flex items-center justify-between gap-3 pt-5">
              <Link
                href={ROUTES.knowledge}
                className="inline-flex items-center gap-1.5 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground"
                data-testid="knowledge-collections-back"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {t('library.collections.header.back')}
              </Link>
              <Button
                type="button"
                size="sm"
                className="h-[34px] gap-2 rounded-lg px-3 font-medium"
                onClick={() => setIsCreateOpen(true)}
                data-testid="knowledge-collections-new"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('library.collections.newCollection')}
              </Button>
            </div>
            <div className="py-5">
              <h1 className={titleClass}>{t('library.collections.title')}</h1>
            </div>
          </header>
          {body}
        </div>
      </div>
    </KnowledgePageShell>
  )
}
